import { ref, reactive, computed, watch } from "@vue/composition-api";
import store from "@/store";
import axiosIns from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { formatDistanceToNow } from "date-fns";
import { zhTW } from "date-fns/locale";

export default function useDeviceManagement() {
  // Use toast
  const toast = useToast();

  // 響應式數據
  const deviceBindings = ref([]);
  const loading = ref(false);
  const searchQuery = ref("");
  const statusFilter = ref(null);
  const supplierFilter = ref(null);
  const currentPage = ref(1);
  const perPage = ref(15);
  const totalRows = ref(0);
  const qrCodeData = ref(null);
  const selectedBinding = ref(null);

  // 選項數據
  const statusOptions = ref([
    { value: null, text: "全部狀態" },
    { value: true, text: "活躍" },
    { value: false, text: "已解綁" }
  ]);

  const supplierOptions = ref([]);

  // 表格字段配置
  const tableFields = ref([
    {
      key: "supplier",
      label: "供應商",
      sortable: false
    },
    {
      key: "device_info",
      label: "設備信息",
      sortable: false
    },
    {
      key: "is_active",
      label: "狀態",
      sortable: true
    },
    {
      key: "bound_at",
      label: "綁定時間",
      sortable: true
    },
    {
      key: "last_accessed_at",
      label: "最後訪問",
      sortable: true
    },
    {
      key: "actions",
      label: "操作",
      sortable: false
    }
  ]);

  // 獲取設備綁定列表
  const fetchDeviceBindings = async () => {
    loading.value = true;
    try {
      const params = {
        page: currentPage.value,
        per_page: perPage.value
      };

      if (searchQuery.value) {
        params.search = searchQuery.value;
      }

      if (statusFilter.value !== null) {
        params.is_active = statusFilter.value;
      }

      if (supplierFilter.value) {
        params.supplier_id = supplierFilter.value.id;
      }

      const response = await axiosIns.get("/user/supplier_device/list", { params });
      
      if (response.data.success) {
        deviceBindings.value = response.data.data.data;
        totalRows.value = response.data.data.total;
        currentPage.value = response.data.data.current_page;
      }
    } catch (error) {
      console.error("獲取設備綁定列表失敗:", error);
      toast({
        component: ToastificationContent,
        props: {
          title: "錯誤",
          icon: "AlertTriangleIcon",
          text: "獲取設備綁定列表失敗",
          variant: "danger"
        }
      });
    } finally {
      loading.value = false;
    }
  };

  // 生成 QR Code
  const generateQrCode = async (supplierId) => {
    try {
      qrCodeData.value = null;
      store.commit("app/TOGGLE_OVERLAY", true);
      
      const response = await axiosIns.post("/user/supplier_device/generate-qr", {
        supplier_id: supplierId
      });

      if (response.data.success) {
        qrCodeData.value = response.data.data;
        store.commit("app/TOGGLE_OVERLAY", false);
        
        // 顯示 QR Code 模態框
        setTimeout(() => {
          document.getElementById("modal-generate-qr").__vue__.$bvModal.show("modal-generate-qr");
        }, 100);

        toast({
          component: ToastificationContent,
          props: {
            title: "成功",
            icon: "CheckIcon",
            text: "QR Code 生成成功",
            variant: "success"
          }
        });
      }
    } catch (error) {
      store.commit("app/TOGGLE_OVERLAY", false);
      console.error("生成 QR Code 失敗:", error);
      
      const errorMessage = error.response?.data?.message || "生成 QR Code 失敗";
      toast({
        component: ToastificationContent,
        props: {
          title: "錯誤",
          icon: "AlertTriangleIcon",
          text: errorMessage,
          variant: "danger"
        }
      });
    }
  };

  // 解除設備綁定
  const unbindDevice = async (bindingId) => {
    try {
      const response = await axiosIns.post(`/user/supplier_device/unbind/${bindingId}`);
      
      if (response.data.success) {
        toast({
          component: ToastificationContent,
          props: {
            title: "成功",
            icon: "CheckIcon",
            text: "設備綁定已解除",
            variant: "success"
          }
        });
        
        // 刷新列表
        await fetchDeviceBindings();
      }
    } catch (error) {
      console.error("解除設備綁定失敗:", error);
      
      const errorMessage = error.response?.data?.message || "解除設備綁定失敗";
      toast({
        component: ToastificationContent,
        props: {
          title: "錯誤",
          icon: "AlertTriangleIcon",
          text: errorMessage,
          variant: "danger"
        }
      });
    }
  };

  // 搜索處理
  const handleSearch = () => {
    currentPage.value = 1;
    fetchDeviceBindings();
  };

  // 篩選處理
  const handleFilter = () => {
    currentPage.value = 1;
    fetchDeviceBindings();
  };

  // 清除篩選
  const clearFilters = () => {
    searchQuery.value = "";
    statusFilter.value = null;
    supplierFilter.value = null;
    currentPage.value = 1;
    fetchDeviceBindings();
  };

  // 刷新數據
  const refreshData = () => {
    fetchDeviceBindings();
  };

  // 顯示解綁模態框
  const showUnbindModal = (binding) => {
    selectedBinding.value = binding;
    document.getElementById("modal-unbind-confirm").__vue__.$bvModal.show("modal-unbind-confirm");
  };

  // 確認解綁
  const confirmUnbind = () => {
    if (selectedBinding.value) {
      unbindDevice(selectedBinding.value.id);
      selectedBinding.value = null;
    }
  };

  // 查看詳情
  const viewDetails = (binding) => {
    // 這裡可以實現查看詳情的邏輯
    console.log("查看設備綁定詳情:", binding);
  };

  // 顯示統計信息
  const showStatistics = async () => {
    try {
      const response = await axiosIns.get("/user/supplier_device/statistics");
      
      if (response.data.success) {
        const stats = response.data.data;
        
        toast({
          component: ToastificationContent,
          props: {
            title: "設備綁定統計",
            icon: "BarChartIcon",
            text: `總綁定數: ${stats.total_bindings}, 活躍: ${stats.active_bindings}, 最近7天新增: ${stats.recent_bindings}`,
            variant: "info"
          }
        });
      }
    } catch (error) {
      console.error("獲取統計信息失敗:", error);
    }
  };

  // 格式化日期時間
  const formatDateTime = (dateTime) => {
    if (!dateTime) return "-";
    return new Date(dateTime).toLocaleString("zh-TW");
  };

  // 獲取相對時間
  const getTimeAgo = (dateTime) => {
    if (!dateTime) return "";
    return formatDistanceToNow(new Date(dateTime), { 
      addSuffix: true, 
      locale: zhTW 
    });
  };

  // 監聽頁面變化
  watch(currentPage, () => {
    fetchDeviceBindings();
  });

  return {
    // 數據
    deviceBindings,
    loading,
    searchQuery,
    statusFilter,
    supplierFilter,
    statusOptions,
    supplierOptions,
    currentPage,
    perPage,
    totalRows,
    qrCodeData,
    selectedBinding,
    
    // 表格配置
    tableFields,
    
    // 方法
    fetchDeviceBindings,
    generateQrCode,
    unbindDevice,
    handleSearch,
    handleFilter,
    clearFilters,
    refreshData,
    showUnbindModal,
    confirmUnbind,
    viewDetails,
    showStatistics,
    formatDateTime,
    getTimeAgo
  };
}
